import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { authAPI } from '../../services/api';

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  
  const { state } = location;
  const { email, role, otpType } = state || {};
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    if (!email) {
      toast.error('Email not found. Please try again.');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await authAPI.verifyOTP(email, data.otp,otpType,role?.toUpperCase());
      
      if (response.data.success) {
        toast.success(otpType === 'PASSWORD_RESET' ? 'OTP verified! Set your new password.' : 'Email verified successfully!');
        
        if (otpType === 'PASSWORD_RESET') {
          navigate('/reset-password', { state: { email, role } });
        } else {
          navigate('/login', { state: { role } });
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid OTP';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (!email) return;

    setResending(true);

    try {
      const response = await authAPI.resendOtp(email, otpType || 'EMAIL_VERIFICATION');
      toast.success('OTP resent to your email!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resend OTP';
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <PublicLayout title="Verify OTP" showBack>
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <div className="card">
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Icon name="alert-circle" size={48} color="var(--red)" />
              <h3 style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '700',
                fontSize: '20px',
                color: 'var(--text)',
                marginBottom: '8px'
              }}>
                Email Not Found
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
                Please start the password reset process again.
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={() => navigate('/forgot-password', { state: { role } })}
                style={{ width: '100%' }}
              >
                Start Over
              </Button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout title="Verify OTP" showBack>
      <div style={{ maxWidth: '400px', margin: '0 auto' }}>
        <div className="card">
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'var(--primary-light)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Icon name="shield-check" size={32} color="var(--primary)" />
            </div>
            <h2 style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '700',
              fontSize: '24px',
              color: 'var(--text)',
              marginBottom: '8px'
            }}>
              Verify Your Email
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              We've sent a verification code to<br />
              <strong>{email}</strong>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* OTP Input */}
            <div>
              <label className="label">Verification Code *</label>
              <input
                {...register('otp', { 
                  required: 'Verification code is required',
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: 'OTP must be 6 digits'
                  }
                })}
                type="text"
                placeholder="Enter 6-digit code"
                maxLength={6}
                style={{
                  width: '100%',
                  textAlign: 'center',
                  fontSize: '20px',
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  borderColor: errors.otp ? 'var(--red)' : 'var(--border)'
                }}
              />
              {errors.otp && (
                <p className="text-red-500">{errors.otp.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={submitting}
              style={{ width: '100%', padding: '12px' }}
            >
              Verify Email
            </Button>

            {/* Resend OTP */}
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resending}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: resending ? 'var(--text-muted)' : 'var(--primary)',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: resending ? 'not-allowed' : 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  {resending ? 'Sending...' : 'Resend Code'}
                </button>
              </span>
            </div>

            {/* Back to Login */}
            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/login', { state: { role } })}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary)',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                ← Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
};

export default OTPVerification;
